import { $getRoot, EditorState, LexicalEditor } from 'lexical';
import { LexicalAbbreviationValues, ABBREVIATION_CHANGE } from 'modules/Lexical/Plugins/AbbreviationPlugin';
import { $selectAbbreviation } from 'modules/Lexical/Plugins/AbbreviationPlugin/utils';
import { BASE_PROPS_COMMAND, BaseProps } from 'modules/Lexical/Plugins/BasePropsPlugin';
import { FONT_COMMAND, FontProps } from 'modules/Lexical/Plugins/FontPlugin';
import { RefObject, useCallback, useEffect, useMemo, useRef } from 'react';
import { EditorHook, EditorProps, EditorSetters } from 'components/ArtboardAssets/CallToAction/editor';
import { SizeResetHook } from 'components/ArtboardAssets/CallToAction/hooks/useSizeReset';
import * as Constants from 'const';
import * as Models from 'models';
import { usePropsUpdater } from './usePropsUpdater';

export type LexicalOnChangeCTA = {
  abbreviations: (values: LexicalAbbreviationValues) => void;
  baseProps: (values: BaseProps) => void;
  font: (values: FontProps) => void;
};

export type LexicalCallToActionEditorHook = EditorHook & {
  editorRef: RefObject<LexicalEditor>;
  onChange: LexicalOnChangeCTA;
  onStopEditing: (state: EditorState) => void;
};

export const useLexicalCallToActionEditor = (
  sizeResetHook: SizeResetHook,
  editorWrap: React.RefObject<HTMLDivElement>,
): LexicalCallToActionEditorHook => {
  const ref = useRef<LexicalEditor>(null);
  const returnFocusToEditor = useCallback(() => {
    ref.current?._rootElement?.focus();
  }, []);

  const { resetHeightInSetters, resetHeightOnEffect } = sizeResetHook;
  const { props, updateProps } = usePropsUpdater<EditorProps>({
    abbreviationId: undefined,
    abbreviationTerm: undefined,
    fontColor: undefined,
    fontFamily: undefined,
    fontSize: undefined,
    charStyle: [],
    scriptStyle: undefined,
  });

  const setters = useMemo((): EditorSetters => ({
    abbreviationId: (id: string | undefined) => ref.current?.dispatchCommand(ABBREVIATION_CHANGE, id),
    fontColor: brandColor => ref.current?.dispatchCommand(FONT_COMMAND.BRAND_COLOR, brandColor),
    fontFamily: (
      brandFont: Models.BrandFontMap,
      characterStyle: Models.CharacterStyleMap,
    ) => ref.current?.dispatchCommand(FONT_COMMAND.BRAND_FONT, { brandFont, characterStyle }),
    fontSize: (value: number): void => {
      const minHeight = Constants.CallToActionLineHeightModifier * value;
      resetHeightInSetters(minHeight);
      ref.current?.dispatchCommand(FONT_COMMAND.SIZE, value);
    },
    charStyle: (style: Constants.CharStyle) => ref.current?.dispatchCommand(
      FONT_COMMAND.CHAR_STYLE,
      style,
    ),
    scriptStyle: (value: Constants.ScriptType) => ref.current?.dispatchCommand(BASE_PROPS_COMMAND.SCRIPT_STYLE, value),
  }), [resetHeightInSetters]);

  const onChange: LexicalOnChangeCTA = useMemo(() => ({
    abbreviations: values => updateProps({
      abbreviationId: values.abbreviationId,
      abbreviationTerm: values.abbreviationTerm,
    }),
    baseProps: (values: BaseProps) => updateProps({
      scriptStyle: values.scriptStyle,
    }),
    font: values => updateProps({
      fontColor: values.brandColor,
      fontFamily: values.brandFont,
      fontSize: values.size,
      charStyle: values.charStyle,
    }),
  }), [updateProps]);

  const lastEditableStateRef = useRef<EditorState | null>(null);

  const onStopEditing = useCallback((state: EditorState) => {
    lastEditableStateRef.current = state;
  }, []);

  const saveStateToDocument = useCallback((document: Models.CallToActionMap): Models.CallToActionMap => {
    const editorState = lastEditableStateRef.current;
    let lexicalState: string | null = null;
    let name = '';
    if (editorState) {
      lexicalState = JSON.stringify(editorState);
      name = editorState.read(() => $getRoot().getTextContent());
    }

    return document.withMutations(doc => doc
      .set('lexicalState', lexicalState)
      .set('name', name),
    );
  }, []);

  const isSameStateInDocument = useCallback((
    a: Models.CallToActionMap,
    b: Models.CallToActionMap,
  ) => {
    return a.get('lexicalState') === b.get('lexicalState');
  }, []);

  useEffect(() => {
    const { current } = editorWrap;
    if (current) {
      current.style.height = 'auto';
    }
    resetHeightOnEffect();
  }, [ref.current?.getEditorState()]);

  const setCursorOnAbbreviation = (abbreviationId: string, abbreviationNumber: number | undefined): boolean => {
    let results = false;
    ref.current?.update(() => {
      results = $selectAbbreviation(abbreviationId, abbreviationNumber);
    }, { discrete: true });

    return results;
  };

  const applySelection = (): void => { };

  return {
    editorRef: ref,
    onChange,
    onStopEditing,
    returnFocusToEditor,
    saveStateToDocument,
    isSameStateInDocument,
    setCursorOnAbbreviation,
    applySelection,
    setters,
    props,
  };
};
